/**
 * The state of the flags in open documents (#316): open or addressed, the
 * reply, a linked task. The document body only says which flags exist; their
 * state lives on the server and is cached here per registered document. It
 * refreshes when that document's autosave lands (a new flag gets its row on
 * that save) and after any undo or redo, which can change flags anywhere.
 */
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { CONTENT_SAVED_EVENT } from '../lib/api';
import { HISTORY_CHANGED_EVENT } from '../lib/history';
import { SOURCE_ROUTES, type SourceType } from '../lib/excerpts';
import {
  BUILT_IN_FLAG_TYPES, customToDef, fetchFlagStates, fetchFlagTypes, type FlagState, type FlagTypeDef, applyBuiltInOverrides, fetchBuiltInOverrides } from '../lib/flags';

export interface FlagSourceRef { type: SourceType; id: string }
type Listener = () => void;
const keyOf = (r: FlagSourceRef) => `${r.type}:${r.id}`;

export class FlagStore {
  private states = new Map<string, FlagState>();
  private sources = new Map<string, { ref: FlagSourceRef; count: number }>();
  private typeDefs: FlagTypeDef[] = BUILT_IN_FLAG_TYPES;
  private typesLoaded = false;
  private listeners = new Set<Listener>();
  private version = 0;

  subscribe = (l: Listener) => { this.listeners.add(l); return () => { this.listeners.delete(l); }; };
  getVersion = () => this.version;
  private emit() { this.version++; for (const l of this.listeners) l(); }

  get(id: string): FlagState | undefined { return this.states.get(id); }

  /** Built-ins, then custom types in position order (archived ones too, so their flags still render). */
  types(): FlagTypeDef[] {
    if (!this.typesLoaded) { this.typesLoaded = true; void this.loadTypes(); }
    return this.typeDefs;
  }

  async loadTypes(): Promise<void> {
    this.typesLoaded = true;
    try {
      // Built-in overrides (#332) are a nicety: without them the defaults show.
      const [rows, overrides] = await Promise.all([fetchFlagTypes(), fetchBuiltInOverrides().catch(() => [])]);
      this.typeDefs = [...applyBuiltInOverrides(BUILT_IN_FLAG_TYPES, overrides), ...rows.map(customToDef)];
      this.emit();
    } catch { /* keep what we have; built-ins always work */ }
  }

  /** Keep this document's flag state loaded until the returned function is called. */
  registerSource(ref: FlagSourceRef): () => void {
    const key = keyOf(ref);
    const entry = this.sources.get(key);
    if (entry) entry.count++;
    else this.sources.set(key, { ref, count: 1 });
    void this.refreshSource(ref);
    return () => {
      const e = this.sources.get(key);
      if (e && --e.count <= 0) this.sources.delete(key);
    };
  }

  async refreshSource(ref: FlagSourceRef): Promise<void> {
    try {
      const list = await fetchFlagStates(ref.type, ref.id);
      for (const s of list) this.states.set(s.id, s);
      this.emit();
    } catch { /* offline or gone: the gutter keeps the last state it had */ }
  }

  setLocal(s: FlagState): void {
    this.states.set(s.id, s);
    this.emit();
  }

  /** States from a list (Find, the meeting panel), so their flags can be opened without the document. */
  seed(list: FlagState[]): void {
    if (list.length === 0) return;
    for (const s of list) {
      this.states.set(s.id, { id: s.id, type: s.type, person_id: s.person_id, status: s.status, reply: s.reply, addressed_at: s.addressed_at, task_id: s.task_id });
    }
    this.emit();
  }

  onContentSaved(path: string): void {
    for (const { ref } of this.sources.values()) {
      if (path === `${SOURCE_ROUTES[ref.type].path}/${ref.id}`) void this.refreshSource(ref);
    }
  }

  /** After undo/redo or a flag-type change: reload everything that is open. */
  invalidate(): void {
    void this.loadTypes();
    for (const { ref } of this.sources.values()) void this.refreshSource(ref);
  }
}

const Ctx = createContext<FlagStore | null>(null);

export function FlagStateProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => new FlagStore(), []);
  useEffect(() => {
    const onSaved = (e: Event) => store.onContentSaved((e as CustomEvent<{ path: string }>).detail?.path ?? '');
    const onHistory = () => store.invalidate();
    window.addEventListener(CONTENT_SAVED_EVENT, onSaved);
    window.addEventListener(HISTORY_CHANGED_EVENT, onHistory);
    return () => {
      window.removeEventListener(CONTENT_SAVED_EVENT, onSaved);
      window.removeEventListener(HISTORY_CHANGED_EVENT, onHistory);
    };
  }, [store]);
  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}

/** The app-wide store; a private one outside the provider (tests, previews). */
const fallback = new FlagStore();
export function useFlagStore(): FlagStore {
  return useContext(Ctx) ?? fallback;
}

/** Re-renders when anything in the store changes. */
export function useFlagStoreVersion(): number {
  const store = useFlagStore();
  return useSyncExternalStore(store.subscribe, store.getVersion);
}

export function useFlagState(id: string): FlagState | undefined {
  const store = useFlagStore();
  useSyncExternalStore(store.subscribe, store.getVersion);
  return store.get(id);
}
